import { useDistributionOptions, Distribution } from "@ls/distribution";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setGridInstance } from "./actions";
import { selectGridInstance } from "state/grid/selector";
import { EntityType } from "@ls/entity-service";
import { useUser } from "@ls/user-module";
import {
  selectCommittedAsOfDateEntity,
  selectModeValue,
  selectScenarioValue,
} from "state/selections/selector";
import { produce } from "immer";
import {
  deleteScenarioEntities,
  fetchScenarioState,
  submitScenarioEntities,
} from "state/scenario/actions";
import SelectionState from "state/selections/SelectionState";
import { useShowPrompt } from "@ls/react-prompt";
import { startRequests } from "state/app/actions";
import { useToast } from "@ls/react-toast";
import { selectScenarioEntityIds } from "state/scenario/selector";
import { throwError } from "@ls/error-module";
import Mode from "state/selections/Mode";
import Input from "@ls/react-input";
import styled from "styled-components";
import Button from "@ls/react-button";
import { Cell, Grid } from "styled-css-grid";
import { useAllColumnDefinitions } from "./hooks";
import { format } from "date-fns";
import { selectDisableIntersperse } from "state/app/selector";

export const useGridInstance = () => {
  const dispatch = useDispatch();
  return [
    useSelector(selectGridInstance),
    useCallback(
      (gridInstance) => dispatch(setGridInstance(gridInstance)),
      [dispatch]
    ),
  ];
};

export const useProcessColumnStateCallback = () => {
  const columnDefs = useAllColumnDefinitions();
  const disableIntersperse = useSelector(selectDisableIntersperse);
  return useCallback(
    ({ columnState: columnStatesArr, columnApi, api }) => {
      const processedColStates = produce(
        columnStatesArr,
        (columnStatesArrDraft) => {
          const columnStateByColId = columnStatesArrDraft.reduce(
            (map, colState) => map.set(colState.colId, colState),
            new Map()
          );
          /**
           * This takes care of sbs columns
           */
          columnDefs
            .filter(({ colId }) => {
              // Check if the column is a scenario column
              const isScenarioColumn = colId.includes("scenario");
              // Include the column if it is not in columnStateByColId or if it is a scenario column
              return disableIntersperse
                ? !columnStateByColId.has(colId)
                : !columnStateByColId.has(colId) || isScenarioColumn;
            })
            .forEach((colDef) => {
              //unintentionally hidden column
              if (colDef.X_sbs) {
                //if column is an sbs column, find its original column (aka non-workspace column)
                const originalIdx = columnStatesArrDraft.findIndex(
                  (colState) => colState.colId === colDef.X_originalColId
                );
                const originalColumnState =
                  columnStatesArrDraft[originalIdx] ?? {};
                if (originalIdx !== -1) {
                  columnStatesArrDraft.splice(originalIdx + 1, 0, {
                    //add an sbs state next to original column's state with a similar sbs column state
                    colId: colDef.colId,
                    hide: originalColumnState.hide ?? false,
                    width: originalColumnState.width,
                    pinned: originalColumnState.pinned,
                  });
                } else {
                  console.warn("original column not found", colDef);
                }
              }
            });
        }
      );
      return processedColStates;
    },
    [columnDefs, disableIntersperse]
  );
  // console.log('columns draft',columnStatesArrDraft)
};

const MAX_RAP_REPORT_COUNT = Number(process.env.REACT_APP_MAX_RAP_REPORT_COUNT);
const RAP_REPORT_PDF_SERVICE = process.env.REACT_APP_RAP_REPORT_PDF_SERVICE;

const Choices = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin: 10px 0 0 0;
  & > div:first-child {
    margin: 0 10px 0 0;
  }
`;

const EntityIDCell = styled(Cell)``;
const LabelCell = styled(Cell)``;
const Entities = styled(Grid).attrs({ columns: "10ch 1fr", gap: 0 })`
  margin-bottom: 10px;
  max-height: 65vh;
  overflow: auto;
`;

const SubmitScenarioPrompt = (props) => {
  const dispatch = useDispatch();
  const { clearPrompt, selectedRows } = props;
  const [scenarioName, setScenarioName] = useState("");
  const { username } = useUser();
  const toast = useToast();
  const submitScenarioEntitiesCallback = useCallback(async () => {
    clearPrompt();
    const response = await dispatch(
      submitScenarioEntities(
        scenarioName,
        selectedRows.map(({ entityId }) => entityId),
        username
      )
    );
    if (response?.success) {
      toast("Entities successfully submitted to CRD.");
    } else {
      throwError(JSON.stringify(response));
    }
    dispatch(startRequests());
  }, [scenarioName, selectedRows, username, toast, dispatch, clearPrompt]);

  useEffect(() => {
    const { document } = window;
    const eventListener = (event) => {
      const { code } = event;
      if (code === "Enter") {
        submitScenarioEntitiesCallback();
      }
    };
    document.body.addEventListener("keydown", eventListener);
    return () => document.body.removeEventListener("keydown", eventListener);
  }, [submitScenarioEntitiesCallback]);
  return (
    <>
      <Entities>
        {selectedRows.map(({ entityId, name, longName }) => [
          <EntityIDCell key={entityId}>{entityId}</EntityIDCell>,
          <LabelCell key={`${entityId}-name`}>{name ?? longName}</LabelCell>,
        ])}
      </Entities>
      <Input
        onChange={setScenarioName}
        value={scenarioName}
        label="Scenario Name:"
        placeholder="New name..."
      />
      <Choices>
        <Button onButtonClick={clearPrompt} label={"Cancel"} />
        <Button
          onButtonClick={submitScenarioEntitiesCallback}
          label={"Accept"}
          variation={3}
        />
      </Choices>
    </>
  );
};
export const useContextMenuEffect = () => {
  const [grid] = useGridInstance();
  const { username } = useUser();
  const asOfDateEntity = useSelector(selectCommittedAsOfDateEntity);
  const selectedScenarioValue = useSelector(
    selectScenarioValue(SelectionState.COMMITTED)
  );
  const mode = useSelector(selectModeValue(SelectionState.COMMITTED));
  const scenarioEntityIds = useSelector(selectScenarioEntityIds);
  const prompt = useShowPrompt();
  const dispatch = useDispatch();
  const { distribution } = useDistributionOptions();
  const toast = useToast();
  useEffect(() => {
    const { in2AppTalk, NameValuePair } = window;
    if (!grid?.api) return;

    grid.api.gridOptionsWrapper.setProperty("getContextMenuItems", () => {
      const selectedRows = grid.api.getSelectedRows() ?? [];
      const accountRows = selectedRows.filter(
        ({ type }) => type !== EntityType.BMARK
      );
      const result = [
        "copy",
        "copyWithHeaders",
        "separator",
        {
          name: "View Accounts",
          disabled: selectedRows.length < 1,
          action: () => {
            //the name is SSID_LIST and the value is csv of entityIds. This is intentional.
            const msg = [
              new NameValuePair(
                "SSID_LIST",
                selectedRows.map(({ entityId }) => entityId).join(",")
              ),
            ];
            in2AppTalk
              .getInstance()
              .publish(in2AppTalk.MSG_TYPE.PRIMARY_STACKED_ITEM_CHANGE, msg);
          },
        },
      ];
      //VN does not yet support RAP reports
      if (distribution !== Distribution.vaughannelson) {
        result.push({
          name: (() => {
            if (accountRows.length === 1) {
              return "Generate RAP Report";
            }
            if (
              accountRows.length > 1 &&
              accountRows.length <= MAX_RAP_REPORT_COUNT
            ) {
              return "Generate RAP Reports";
            }
            if (
              accountRows.length > 1 &&
              accountRows.length > MAX_RAP_REPORT_COUNT
            ) {
              return `Generate RAP Reports (MAX: ${MAX_RAP_REPORT_COUNT})`;
            }
            return "Generate RAP Report(s)";
          })(),
          disabled:
            accountRows.length === 0 ||
            accountRows.length > MAX_RAP_REPORT_COUNT,
          action: () => {
            const asOfDateString = asOfDateEntity
              ? format(asOfDateEntity, "MM/dd/yyyy")
              : "";
            const { in2 } = window;
            const openUrl =
              typeof in2 !== "undefined"
                ? (url) => in2.OpenHtml(null, url)
                : (url) => window.open(url, "_blank");
            accountRows.forEach(({ entityId }) => {
              const url = `${RAP_REPORT_PDF_SERVICE}?entityId=${entityId}&asOfDate=${asOfDateString}&uid=${username}&attachment=false&rapType=FULL_RAP`;
              openUrl(url);
            });
          },
        });
      }

      if (mode === Mode.SCENARIOS) {
        result.push("separator");
        result.push({
          name: "Delete Selected Account Trades",
          disabled: selectedRows.length === 0,
          action: async () => {
            if (selectedRows.length === scenarioEntityIds.length) {
              throwError("You can not leave an empty scenario.");
              return;
            }
            const scenarioState = await dispatch(
              fetchScenarioState(selectedScenarioValue)
            );
            if (scenarioState === "RELEASED" || scenarioState === "SUBMITTED") {
              prompt({
                title: `Error`,
                desc: (
                  <span>
                    The scenario is in <b>{scenarioState}</b> state and cannot
                    be modified.
                  </span>
                ),
                choices: [
                  {
                    label: "OK",
                    primary: true,
                  },
                ],
              });
              return;
            }
            prompt({
              title: `Delete Selected Entity Trades?`,
              desc: (
                <span>
                  This will permanently delete all trades for:
                  <Entities>
                    {selectedRows.map(({ entityId, name, longName }) => [
                      <EntityIDCell key={entityId}>{entityId}</EntityIDCell>,
                      <LabelCell key={`${entityId}-name`}>
                        {name ?? longName}
                      </LabelCell>,
                    ])}
                  </Entities>
                </span>
              ),
              choices: [
                {
                  label: "Cancel",
                },
                {
                  label: "Accept",
                  primary: true,
                  onClick: async () => {
                    const success = await dispatch(
                      deleteScenarioEntities(
                        selectedScenarioValue,
                        selectedRows.map(({ entityId }) => entityId),
                        username
                      )
                    );
                    if (success) {
                      toast("Entities successfully deleted.");
                      dispatch(startRequests());
                    } else {
                      toast.error("Problem deleting entities.");
                    }
                  },
                },
              ],
            });
          },
        });
        result.push({
          name: "Release and Send Selected Account Trades",
          disabled: selectedRows.length === 0,
          action: async () => {
            if (selectedRows.length === scenarioEntityIds?.length) {
              throwError("You can not leave an empty scenario.");
              return;
            }
            const scenarioState = await dispatch(
              fetchScenarioState(selectedScenarioValue)
            );
            if (scenarioState === "RELEASED" || scenarioState === "SUBMITTED") {
              prompt({
                title: `Error`,
                desc: (
                  <span>
                    The scenario is in <b>{scenarioState}</b> state and cannot
                    be modified.
                  </span>
                ),
                choices: [
                  {
                    label: "OK",
                    primary: true,
                  },
                ],
              });
              return;
            }
            prompt({
              title: `Release and Send to CRD Account Trades?`,
              desc: (props) => (
                <SubmitScenarioPrompt selectedRows={selectedRows} {...props} />
              ),
              choices: [],
            });
          },
        });
      }
      return result;
    });
  }, [
    asOfDateEntity,
    dispatch,
    distribution,
    grid,
    mode,
    prompt,
    scenarioEntityIds,
    selectedScenarioValue,
    toast,
    username,
  ]);
};
